/**
 * Onboarding consultivo "Empezar poco a poco" (Idea 15 ROADMAP).
 *
 * Flow de 3 pasos antes de crear el cobro real. Reduce paralisis del
 * tesorero que aterriza en /nuevo sin saber qué vertical elegir.
 *
 * Step 1 · qué tipo de entidad eres
 * Step 2 · cuántos miembros gestionas
 * Step 3 · qué cobro empezar primero (sugerencia + alternativas)
 *
 * Al elegir, redirige a /nuevo?modo=X con preset.
 */
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import TopBar from '../components/TopBar';


type EntityType = 'hermandad' | 'ampa' | 'club' | 'pena' | 'asociacion';
type Size = 'pequena' | 'mediana' | 'grande';
type Vertical =
  | 'membership_quota'
  | 'group_gift'
  | 'event_crowdfund'
  | 'raffle_share'
  | 'papeleta_sitio'
  | 'ticket_share'
  | 'caseta';


const ENTITY_OPTIONS: { v: EntityType; label: string; desc: string; emoji: string }[] = [
  { v: 'hermandad', label: 'Hermandad o cofradía', desc: 'Cuotas, sorteo de papeletas, gastos de Semana Santa', emoji: '✝' },
  { v: 'ampa', label: 'AMPA o asociación de padres', desc: 'Cuotas, excursiones, libros, eventos', emoji: '📚' },
  { v: 'club', label: 'Club deportivo', desc: 'Cuotas por categoría, campus, equipación, viajes', emoji: '⚽' },
  { v: 'pena', label: 'Peña o grupo informal', desc: 'Caseta de feria, viaje, regalo conjunto', emoji: '🎉' },
  { v: 'asociacion', label: 'Asociación · otra entidad', desc: 'Cuotas de socio, eventos benéficos, crowdfunding', emoji: '🤝' },
];


const SIZE_OPTIONS: { v: Size; label: string; desc: string }[] = [
  { v: 'pequena', label: 'Pequeña', desc: 'Hasta 20 miembros activos' },
  { v: 'mediana', label: 'Mediana', desc: '20 a 100 miembros' },
  { v: 'grande', label: 'Grande', desc: 'Más de 100 miembros' },
];


/**
 * Sugerencia del primer cobro: la regla más simple y de mayor valor
 * inmediato según la combinación entidad + tamaño. Inspirada en
 * "no empezar con todo, sino con una campaña sencilla" (dosier).
 */
function suggestFirst(entity: EntityType, size: Size): { vertical: Vertical; rationale: string } {
  if (entity === 'hermandad') {
    return size === 'pequena'
      ? { vertical: 'membership_quota', rationale: 'Empieza por la cuota mensual de los hermanos · es el flujo recurrente más simple y muestra valor desde el primer mes.' }
      : { vertical: 'raffle_share', rationale: 'Una rifa benéfica es el caso más rentable y visible para la junta · genera tracción rápida.' };
  }
  if (entity === 'ampa') {
    return { vertical: 'membership_quota', rationale: 'La cuota anual de socios es el primer flujo que toda AMPA paga · empieza ahí y los siguientes (excursión, libros) los añades cuando ya tengas el hábito.' };
  }
  if (entity === 'club') {
    return size === 'pequena'
      ? { vertical: 'membership_quota', rationale: 'Cuota mensual por categoría (sub-12, sub-14…) · es lo más recurrente y donde el club gana visibilidad.' }
      : { vertical: 'event_crowdfund', rationale: 'Un viaje a torneo es el caso más sentido por los padres · prueba con uno solo antes de meter todas las cuotas.' };
  }
  if (entity === 'pena') {
    return { vertical: 'group_gift', rationale: 'Empieza con algo concreto · un regalo conjunto o el bote de la cena. Confianza inmediata, sin compromiso largo.' };
  }
  return { vertical: 'membership_quota', rationale: 'La cuota de socio es la base de cualquier asociación · empieza ahí y añade eventos puntuales cuando ya tengas el flujo establecido.' };
}


const VERTICAL_INFO: Record<Vertical, { label: string; desc: string; mode: string }> = {
  membership_quota: { label: 'Cuota de socio', desc: 'Mensual, trimestral o anual · cada miembro paga su parte', mode: 'membership_quota' },
  group_gift: { label: 'Regalo conjunto', desc: 'Bote para un regalo, una cena, una salida común', mode: 'group_gift' },
  event_crowdfund: { label: 'Evento puntual', desc: 'Viaje, campus, excursión · todos aportan a un objetivo', mode: 'event_crowdfund' },
  raffle_share: { label: 'Sorteo benéfico', desc: 'Papeletas digitales con QR · cumple Decreto Andalucía', mode: 'raffle_share' },
  papeleta_sitio: { label: 'Papeleta de sitio', desc: 'Para hermandades · cada cofrade su silla con QR', mode: 'papeleta_sitio' },
  ticket_share: { label: 'Entrada en grupo', desc: 'Reserva colectiva con asignación por persona', mode: 'ticket_share' },
  caseta: { label: 'Caseta de feria', desc: 'Bonos prepago, reserva de mesa y check-in de invitados', mode: 'caseta' },
};


export default function StartFlowPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [entity, setEntity] = useState<EntityType | null>(null);
  const [size, setSize] = useState<Size | null>(null);
  const [vertical, setVertical] = useState<Vertical | null>(null);

  const suggestion = entity && size ? suggestFirst(entity, size) : null;

  const start = () => {
    const chosen = vertical || suggestion?.vertical || 'membership_quota';
    const mode = VERTICAL_INFO[chosen].mode;
    navigate(`/nuevo?modo=${mode}&from=empezar`);
  };

  return (
    <div className="min-h-screen bg-surface text-ink font-sans antialiased">
      <TopBar contextLabel="Empezar guiado" rightCta={{ label: 'Crear directo', to: '/nuevo' }} />
      <header className="bg-paper border-b border-line">
        <div className="max-w-2xl mx-auto px-6 py-7">
          <div className="flex items-baseline justify-between">
            <h1 className="font-display font-bold text-[32px] text-ink tracking-[-0.02em]">
              Empezar poco a poco
            </h1>
            <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-500">
              Paso {step} de 3
            </span>
          </div>
          <p className="text-[13.5px] text-ink-500 mt-1">
            No te abrumamos · 3 preguntas y te dejamos una primera campaña concreta para arrancar.
          </p>
        </div>
        {/* Progreso */}
        <div className="max-w-2xl mx-auto px-6 pb-3">
          <div className="h-1.5 bg-line rounded-full overflow-hidden">
            <div
              className="h-full bg-brand rounded-full transition-all"
              style={{ width: `${(step / 3) * 100}%` }}
            />
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-8">
        {step === 1 && (
          <section>
            <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-500 mb-3">
              · Paso 1
            </div>
            <h2 className="font-display font-bold text-[24px] text-ink tracking-[-0.02em] mb-6">
              ¿Qué tipo de entidad eres?
            </h2>
            <ul className="space-y-2">
              {ENTITY_OPTIONS.map((opt) => (
                <li key={opt.v}>
                  <button
                    type="button"
                    onClick={() => { setEntity(opt.v); setStep(2); }}
                    className={`w-full text-left px-5 py-4 rounded-card border-[1.5px] transition flex items-start gap-3 ${
                      entity === opt.v
                        ? 'border-brand bg-brand-50'
                        : 'border-line bg-paper hover:border-brand'
                    }`}
                  >
                    <span className="text-[22px]" aria-hidden>{opt.emoji}</span>
                    <div>
                      <div className="font-display font-semibold text-[16px] text-ink tracking-[-0.01em]">
                        {opt.label}
                      </div>
                      <div className="text-[13px] text-ink-500 mt-0.5">{opt.desc}</div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}

        {step === 2 && (
          <section>
            <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-500 mb-3">
              · Paso 2
            </div>
            <h2 className="font-display font-bold text-[24px] text-ink tracking-[-0.02em] mb-6">
              ¿Cuántos miembros activos sois?
            </h2>
            <ul className="space-y-2 mb-6">
              {SIZE_OPTIONS.map((opt) => (
                <li key={opt.v}>
                  <button
                    type="button"
                    onClick={() => { setSize(opt.v); setStep(3); }}
                    className={`w-full text-left px-5 py-4 rounded-card border-[1.5px] transition ${
                      size === opt.v
                        ? 'border-brand bg-brand-50'
                        : 'border-line bg-paper hover:border-brand'
                    }`}
                  >
                    <div className="font-display font-semibold text-[16px] text-ink tracking-[-0.01em]">
                      {opt.label}
                    </div>
                    <div className="text-[13px] text-ink-500 mt-0.5">{opt.desc}</div>
                  </button>
                </li>
              ))}
            </ul>
            <button
              type="button"
              onClick={() => setStep(1)}
              className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-500 hover:text-ink"
            >
              ← Atrás
            </button>
          </section>
        )}

        {step === 3 && suggestion && (
          <section>
            <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-500 mb-3">
              · Paso 3 · nuestra recomendación
            </div>
            <h2 className="font-display font-bold text-[24px] text-ink tracking-[-0.02em] mb-2">
              {VERTICAL_INFO[suggestion.vertical].label}
            </h2>
            <div className="bg-olive-50 border border-[#C7D5A8] rounded-card p-5 mb-6">
              <div className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-olive-700 mb-2">
                · Por qué este
              </div>
              <p className="text-[14px] text-ink-700 leading-relaxed">
                {suggestion.rationale}
              </p>
            </div>

            <details className="mb-6">
              <summary className="cursor-pointer font-mono text-[11px] uppercase tracking-[0.18em] text-ink-500 hover:text-ink mb-3">
                · O elige otro vertical
              </summary>
              <ul className="space-y-2 mt-3">
                {Object.entries(VERTICAL_INFO).map(([v, info]) => (
                  <li key={v}>
                    <button
                      type="button"
                      onClick={() => setVertical(v as Vertical)}
                      className={`w-full text-left px-4 py-3 rounded-card border transition ${
                        vertical === v || (!vertical && v === suggestion.vertical)
                          ? 'border-brand bg-brand-50'
                          : 'border-line bg-paper hover:border-brand'
                      }`}
                    >
                      <div className="font-display font-semibold text-[14.5px] text-ink tracking-[-0.01em]">
                        {info.label}
                      </div>
                      <div className="text-[12.5px] text-ink-500 mt-0.5">{info.desc}</div>
                    </button>
                  </li>
                ))}
              </ul>
            </details>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setStep(2)}
                className="px-5 py-3 rounded-xl border-[1.5px] border-line-strong bg-paper text-ink-700 font-semibold text-[14px] hover:border-brand transition"
              >
                ← Atrás
              </button>
              <button
                type="button"
                onClick={start}
                className="flex-1 px-5 py-3 rounded-xl bg-brand text-paper font-semibold text-[14.5px] shadow-btn hover:bg-brand-700 transition"
              >
                Crear este cobro →
              </button>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
